import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Box,
  IconButton,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

//
// ✅ 1️⃣ Define Types
//
type StatusType = "success" | "warning";
type RoleType = "Admin" | "Manager" | "Viewer";

//
// ✅ 2️⃣ Permission Log Interface
//
interface PermissionLog {
  id: number;
  date: string;
  role: RoleType;
  changedBy: string;
  action: string;
  details: string;
  ip: string;
  status: StatusType;
}

//
// ✅ 3️⃣ Status Icon Props
//
interface StatusIconProps {
  type: StatusType;
}

//
// ✅ 4️⃣ Initial Data
//
const initialLogs: PermissionLog[] = [
  {
    id: 1,
    date: "Apr 20, 2024, 2:45 PM",
    role: "Admin",
    changedBy: "Jane Doe",
    action: "Full Access granted",
    details: "Full Access granted",
    ip: "192.168.1.45",
    status: "success",
  },
  {
    id: 2,
    date: "Apr 18, 2024, 11:10 AM",
    role: "Manager",
    changedBy: "John Smith",
    action: "API Access enabled",
    details: "API Access enabled",
    ip: "*.52.113.24",
    status: "success",
  },
  {
    id: 3,
    date: "Apr 17, 2024, 4:52 PM",
    role: "Viewer",
    changedBy: "Michael L.",
    action: "Changed Autonomy Level from Level 1 to Level 2",
    details: "Level 1 to Level 2",
    ip: "192.168.1.51",
    status: "warning",
  },
];

//
// ✅ 5️⃣ Status Icon Component
//
const StatusIcon = ({ type }: StatusIconProps) => {
  return type === "success" ? (
    <CheckCircleIcon fontSize="small" color="success" />
  ) : (
    <FiberManualRecordIcon fontSize="small" sx={{ color: "#d97706" }} />
  );
};

//
// ✅ 6️⃣ Main Component
//
const PermissionLogTable: React.FC = () => {
  const [logs, setLogs] = useState<PermissionLog[]>(initialLogs);

  const handleRoleChange = (
    event: SelectChangeEvent,
    id: number
  ) => {
    const updatedLogs = logs.map((log) =>
      log.id === id
        ? { ...log, role: event.target.value as RoleType }
        : log
    );
    setLogs(updatedLogs);
  };

  return (
    <div className="lt">
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow sx={{ background: "#f6f6f8" }}>
              <TableCell>Date & Time</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Changed By</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>IP Address</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {logs.map((row) => (
              <TableRow key={row.id} hover>
                {/* Date */}
                <TableCell>
                  <Typography fontWeight={500}>
                    {row.date}
                  </Typography>
                </TableCell>

                {/* ✅ Role Select */}
                <TableCell>
                  <Select
                    size="small"
                    value={row.role}
                    onChange={(e) => handleRoleChange(e, row.id)}
                    sx={{
                      minWidth: 120,
                      background: "#f1e9ff",
                      borderRadius: 2,
                      fontWeight: 500,
                    }}
                  >
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="Manager">Manager</MenuItem>
                    <MenuItem value="Viewer">Viewer</MenuItem>
                  </Select>
                </TableCell>

                {/* Changed By */}
                <TableCell>
                  <Typography>{row.changedBy}</Typography>
                </TableCell>

                {/* Action */}
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StatusIcon type={row.status} />
                    <Typography>{row.action}</Typography>
                  </Stack>
                </TableCell>

                {/* Details */}
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StatusIcon type={row.status} />
                    <Typography>{row.details}</Typography>
                  </Stack>
                </TableCell>

                {/* IP */}
                <TableCell>
                  <Typography>{row.ip}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination Footer */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
        >
          <Typography variant="body2">
            1 to {logs.length} of 9
          </Typography>

          <Stack direction="row" spacing={1}>
            <IconButton size="small">{"<"}</IconButton>
            <IconButton size="small">{">"}</IconButton>
          </Stack>
        </Box>
      </TableContainer>
    </div>
  );
};

export default PermissionLogTable;
